import {
  DataGridPro,
  GridActionsCellItem,
  GridColumns,
  GridRowParams,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridValueGetterParams,
} from '@mui/x-data-grid-pro';
import React, { useEffect, useState } from 'react';
import { Box, FormControl, Grid, InputLabel, MenuItem, Paper, Tooltip, Typography } from '@mui/material';
import WO2Button from 'src/common/components/button/Button';
import { ArrowDownward, ArrowUpward, Delete, Edit, Public } from '@mui/icons-material';
import { Props } from '../../container';
import { ReportingParametersDeleteModal } from './ReportingParametersDeleteModal';
import { useHistory } from 'react-router-dom';
import { GetReportParametersRequest, GetReportParametersViewModel, ReportParametersKind, ReportParametersKindName } from '../../store/types';
import { DateTimeFormat, getLocalDateTime, WO2Select } from 'src/common';

export const ReportingParametersTable = (props: Props): JSX.Element => {
  const history = useHistory();

  const [request, setRequest] = useState<GetReportParametersRequest>({
    page: 0,
    pageSize: 25,
    includeSharedAfsl: !props.hasClientEditAdminOnlyPermission,
    kind: 'C',
  });

  const [clickedRow, setClickedRow] = useState<GetReportParametersViewModel | null>(null);
  const [clickedState, setClickedState] = useState<'delete' | null>(null);

  const flagRequestChange = () => {
    const newRequest: GetReportParametersRequest = {
      ...request,
      page: 0,
      pageSize: 20,
    };
    setRequest(newRequest);
  };

  const columns: GridColumns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      renderCell(params) {
        return (
          <Box display="flex" alignItems="center" gap={1}>
            {params.row?.isSystemShared && <Public color="primary" />}
            <Typography>{params.value}</Typography>
          </Box>
        );
      },
    },
    { field: 'createdByName', headerName: 'Created by', flex: 1 },
    {
      field: 'afslName',
      headerName: 'Shared with AFSL',
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => {
        return !params.row.visibleToAfsl ? '' : params.value;
      },
    },
    {
      field: 'kind',
      headerName: 'Kind',
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => (params.value ? ReportParametersKindName[params.value as ReportParametersKind] : 'Unknown'),
    },
    {
      field: 'createdOn',
      headerName: 'Created on',
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => getLocalDateTime(params.value, DateTimeFormat.DateTimeShort),
    },
    {
      field: 'modifiedOn',
      headerName: 'Modified on',
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => getLocalDateTime(params.value, DateTimeFormat.DateTimeShort),
    },
    {
      field: 'Actions',
      type: 'actions',
      sortable: false,
      width: 160,
      getActions: (params: GridRowParams<GetReportParametersViewModel>) => {
        const actions = [
          <GridActionsCellItem
            key="edit"
            onClick={() => history.push(`/parameters/edit/${params.row.id}`)}
            disabled={params.row?.canEdit === false}
            icon={
              <Tooltip title="Edit">
                <Edit />
              </Tooltip>
            }
            label="Edit"
            color="primary"
          />,
        ];

        if (props.hasClientEditAdminOnlyPermission) {
          const label = params.row.isSystemShared ? 'Disable system template' : 'Enable system template';
          actions.push(
            <GridActionsCellItem
              key="system-shared"
              onClick={() => {
                props
                  .setReportingParameterSystemShare({
                    reportParameterId: params.row.id,
                    systemShared: !params.row.isSystemShared,
                  })
                  .then(() => {
                    flagRequestChange();
                  });
              }}
              icon={<Tooltip title={label}>{params.row.isSystemShared ? <ArrowDownward /> : <ArrowUpward />}</Tooltip>}
              label={label}
              color="primary"
            />
          );
        }

        if (params.row.createdById == props.userId) {
          actions.push(
            <GridActionsCellItem
              key="delete"
              onClick={() => {
                setClickedRow(params.row);
                setClickedState('delete');
              }}
              icon={
                <Tooltip title="Delete">
                  <Delete />
                </Tooltip>
              }
              label="Delete"
              color="primary"
            />
          );
        }
        return actions;
      },
    },
  ];

  const tableToolbar = () => {
    return (
      <GridToolbarContainer sx={{ padding: 2, alignItems: 'center' }}>
        <Box>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <FormControl fullWidth>
                <InputLabel id="kind-id">Kind</InputLabel>
                <WO2Select
                  labelId="kind-id"
                  label="Kind"
                  onChange={(e) =>
                    setRequest({
                      ...request,
                      kind: e.target.value as string,
                    })
                  }
                  value={request.kind ?? 'C'}
                >
                  {Object.entries(ReportParametersKindName).map(([key, value]) => (
                    <MenuItem key={key} value={key as ReportParametersKind}>
                      {value}
                    </MenuItem>
                  ))}
                </WO2Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <Box flex={1} />
        <Box>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <GridToolbarColumnsButton />
            </Grid>
            <Grid item>
              <GridToolbarFilterButton />
            </Grid>

            <Grid item>
              <WO2Button variant="contained" onClick={() => history.push('/parameters/create')}>
                Create
              </WO2Button>
            </Grid>
          </Grid>
        </Box>
      </GridToolbarContainer>
    );
  };

  useEffect(() => {
    props.fetchReportingParameters(request);
  }, [request]);

  const closeModal = () => {
    setClickedRow(null);
    setClickedState(null);
  };

  return (
    <>
      <Paper elevation={4}>
        <DataGridPro
          initialState={{
            pagination: {
              page: 0,
              pageSize: 25,
            },
          }}
          componentsProps={{
            toolbar: props,
          }}
          components={{
            Toolbar: tableToolbar,
          }}
          checkboxSelection
          autoHeight
          pagination
          page={request.page}
          getRowId={(row) => row.id}
          rowCount={props.bulk.reportingParametersList?.totalNumberOfRecords ?? 0}
          pageSize={request.pageSize}
          loading={props.fetchReportingParametersProgress.isLoading}
          onPageChange={(page: number) => {
            setRequest({ ...request, page: page });
          }}
          disableSelectionOnClick
          onPageSizeChange={(pageSize: number) => {
            setRequest({ ...request, pageSize: pageSize });
          }}
          paginationMode="server"
          columns={columns}
          rows={props.bulk.reportingParametersList?.results ?? []}
        />
      </Paper>
      {clickedRow && (
        <>
          {(() => {
            switch (clickedState) {
              case 'delete':
                return (
                  <ReportingParametersDeleteModal
                    handleDelete={() =>
                      props.deleteReportingParameter(clickedRow.id).then(() => {
                        flagRequestChange();
                      })
                    }
                    row={clickedRow}
                    isOpen={clickedState === 'delete'}
                    handleClose={closeModal}
                  />
                );
            }
            return <></>;
          })()}
        </>
      )}
    </>
  );
};
